import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Menu, X, Home, Briefcase, FileText, User, Users, UserCheck, Settings, AlertTriangle, ArrowLeft, ChevronsLeft, ChevronsRight, ChevronDown, ChevronRight, Sun, Moon, ScrollText, HelpCircle, Heart, DollarSign, Landmark, Vote, ClipboardList, ClipboardCheck, PiggyBank, CalendarDays, CalendarCheck, MailCheck } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useOrganizations, useUserAccess } from '../hooks/useOrganizations';
import { useTheme } from '@shared/theme/ThemeContext';
import { useFeatureFlags, type FlagMap } from '@shared/flags/FeatureFlagProvider';
import { usePageMetadata } from '../contexts/PageMetadataContext';
import StatefulIcon from './StatefulIcon';
import NotificationsBell from './NotificationsBell';
import ImpersonationBanner from './ImpersonationBanner';
import api from '../lib/api';
import { indexedDbPersister } from '../lib/offline/persister';
import { hasDualViewAccess, switchToCitizenView } from '../utils/switchView';
// Key only — the module is a leaf over api/persister, both already in this chunk.
import { ADMIN_SESSION_SNAPSHOT_KEY } from '../admin/lib/simulation';
import logomark from '../assets/capitol-commons-logomark.svg';
// The transparent wordmark from the marketing site (capitolcommons.ai's own
// asset). NOT the citizen app's cc-logo.png: that file has an opaque
// background — the citizen surfaces mask it with blend modes, but on the
// solid purple sidebar it rendered as a white box.
import ccLogo from '../assets/capitol-commons-wordmark.png';
import { AnalyticsIcon } from './icons';
import { HelpTrigger } from '../help';

// Navigation items
// Note: Menu visibility is controlled by subscription-based permissions
// Items with keys 'non-profits', 'campaign-finance', 'sos-report' are hidden on mobile only
const baseNavigation = [
  { name: 'Home', href: '/dashboard', icon: Home, key: 'dashboard' },
  { name: 'Workspaces', href: '/workspaces', icon: Briefcase, key: 'workspaces' },
  { name: 'Bills', href: '/legislation', icon: FileText, key: 'legislation' },
  { name: 'Budget', href: '/budget', icon: Landmark, key: 'budget' },
  { name: 'Calendar', href: '/calendar', icon: CalendarDays, key: 'calendar' },
  { name: 'Legislators', href: '/legislators', icon: Users, key: 'legislators' },
  { name: 'Candidates', href: '/candidates', icon: UserCheck, key: 'candidates' },
  { name: 'Ballot Initiatives', href: '/ballot-initiatives', icon: ClipboardCheck, key: 'ballot-initiatives' },
  { name: 'Election Calendar', href: '/elections/calendar', icon: CalendarCheck, key: 'elections-calendar' },
  { name: 'Lobbying', href: '/lobbying', icon: ScrollText, key: 'lobbying' },
  { name: 'Non-Profits', href: '/non-profits', icon: Heart, key: 'non-profits' },
  { name: 'Campaign Finance', href: '/campaign-finance', icon: DollarSign, key: 'campaign-finance' },
  { name: 'Reports', href: '/reports', icon: AnalyticsIcon, key: 'sos-report' },
  { name: 'Voter Insights', href: '/voter-registration', icon: Vote, key: 'voter-registration' },
  { name: 'Returned Ballots', href: '/ballot-returns', icon: MailCheck, key: 'ballot-returns' },
  { name: 'Admin Tools', href: '/admin', icon: Settings, key: 'admin' },
  { name: 'Help', href: '/help', icon: HelpCircle, key: 'help' },
];

// Desktop (web) grouped navigation. Mobile continues to use the flat `baseNavigation` above.
type NavLeaf = { name: string; href: string; icon: typeof Home; key: string };
type NavGroup = { type: 'group'; name: string; icon: typeof Home; key: string; children: NavLeaf[] };
type NavEntry = ({ type?: 'item' } & NavLeaf) | NavGroup;

const isNavGroup = (entry: NavEntry): entry is NavGroup => (entry as NavGroup).type === 'group';

const desktopNavigation: NavEntry[] = [
  { name: 'Home', href: '/dashboard', icon: Home, key: 'dashboard' },
  { name: 'Workspaces', href: '/workspaces', icon: Briefcase, key: 'workspaces' },
  { type: 'group', name: 'Legislative Cycle', icon: Landmark, key: 'legislative-cycle', children: [
    { name: 'Bills', href: '/legislation', icon: FileText, key: 'legislation' },
    { name: 'Legislators', href: '/legislators', icon: Users, key: 'legislators' },
    { name: 'Lobbying', href: '/lobbying', icon: ScrollText, key: 'lobbying' },
    { name: 'Budget', href: '/budget', icon: PiggyBank, key: 'budget' },
    { name: 'Calendar', href: '/calendar', icon: CalendarDays, key: 'calendar' },
  ] },
  { type: 'group', name: 'Elections', icon: Vote, key: 'elections', children: [
    { name: 'Candidates', href: '/candidates', icon: UserCheck, key: 'candidates' },
    { name: 'Ballot Initiatives', href: '/ballot-initiatives', icon: ClipboardCheck, key: 'ballot-initiatives' },
    { name: 'Campaign Finance', href: '/campaign-finance', icon: DollarSign, key: 'campaign-finance' },
    { name: 'Voter Data', href: '/voter-registration', icon: ClipboardList, key: 'voter-registration' },
    { name: 'Returned Ballots', href: '/ballot-returns', icon: MailCheck, key: 'ballot-returns' },
    { name: 'Election Calendar', href: '/elections/calendar', icon: CalendarCheck, key: 'elections-calendar' },
  ] },
  { name: 'Reports', href: '/reports', icon: AnalyticsIcon, key: 'sos-report' },
  { name: 'Help', href: '/help', icon: HelpCircle, key: 'help' },
  { name: 'Admin Tools', href: '/admin', icon: Settings, key: 'admin' },
];

// Nav keys for the research pages gated server-side by researchGate
// (backend/src/middleware/requireSubscription.js, #1191).
const RESEARCH_NAV_KEYS = [
  'legislation',
  'legislators',
  'lobbying',
  'budget',
  'non-profits',
  'candidates',
  'ballot-initiatives',
  'campaign-finance',
];

// Role detection functions - Simplified: system_admin, org_admin, or default (no org membership)
const createRoleFunctions = (organizations: any[], hasSubscriptionAccess: boolean) => {
  const getUserRole = (): string => {
    if (organizations.some(org => org.role?.toLowerCase() === 'system_admin')) return 'system_admin';
    if (organizations.some(org => org.role?.toLowerCase() === 'org_admin')) return 'org_admin';
    // No org-level contributor/guest roles anymore - users either manage org or are invited to specific workspaces
    return 'user'; // Default for users without org admin role
  };

  const isSystemAdmin = (): boolean => {
    return organizations.some(org => org.role?.toLowerCase() === 'system_admin');
  };

  const canAccess = (itemKey: string): boolean => {
    const role = getUserRole();

    // Admin Tools: Only system_admin
    if (itemKey === 'admin') {
      return isSystemAdmin();
    }

    // Basic menu items: everyone. Invited workspace contributors and guests
    // see ONLY these (Angie, 2026-10-11) — Calendar and Election Calendar
    // moved below, to subscribed org admins. The dashboard's links into the
    // calendar pages stay (decided: nav only), so the pages remain reachable
    // from Home.
    const basicItems = ['dashboard', 'workspaces', 'help'];
    if (basicItems.includes(itemKey)) {
      return true;
    }

    // Everything below needs an active subscription. `hasSubscriptionAccess`
    // (GET /auth/me) is the same predicate the backend research gate enforces
    // (#1191): an org_admin of an active paid/trial org, or system_admin.
    // Invited workspace contributors/guests do not pass unless they are an org
    // admin of some other paid org; citizen memberships never count.
    // Workspace-only users see only Dashboard / Workspaces / Help above.
    if (!hasSubscriptionAccess) {
      return false;
    }

    // Calendars: org admins of paid orgs (and system admins), like research.
    if (itemKey === 'calendar' || itemKey === 'elections-calendar') {
      return true;
    }

    // Research pages: every entitled user — the API serves them, so the nav
    // does too (#1191).
    if (RESEARCH_NAV_KEYS.includes(itemKey)) {
      return true;
    }

    // Remaining org-level surfaces (Reports, Voter Data, Returned Ballots):
    // org_admin only.
    return role === 'org_admin' || role === 'system_admin';
  };

  return { getUserRole, isSystemAdmin, canAccess };
};

// Get current user from localStorage (basic info only)
const getCurrentUser = (orgCount?: number, apiProfileImageUrl?: string | null) => {
  try {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    return {
      initials: userData.firstName?.charAt(0) || userData.username?.charAt(0) || userData.email?.charAt(0) || 'U',
      name: userData.displayName || userData.firstName || userData.username || userData.email || 'User',
      orgCount: orgCount || userData.organizationCount || 0,
      id: userData.id,
      // Prefer API-provided presigned URL over localStorage (which may be stale/expired)
      profileImageUrl: apiProfileImageUrl ?? userData.profileImageUrl ?? null
    };
  } catch (error) {
    return {
      initials: 'U',
      name: 'User',
      orgCount: orgCount || 0,
      id: null,
      profileImageUrl: apiProfileImageUrl ?? null
    };
  }
};

const MobileFooterNav = ({ active, onBack, onHome, onSettings }: { 
  active: 'home' | 'back' | 'settings';
  onBack: () => void;
  onHome: () => void;
  onSettings: () => void;
}) => (
  <nav
    className="mobile-footer fixed bottom-0 left-0 w-full h-16 border-t border-border-light dark:border-transparent flex justify-around items-center z-50 lg:hidden shadow-nav"
    aria-label="Mobile navigation"
    data-testid="layout-mobile-footer"
  >
    <button
      className="flex flex-col items-center text-text-secondary dark:text-text-dark-secondary hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none transition-colors"
      aria-label="Go back"
      onClick={onBack}
    >
      <ArrowLeft className="w-5 h-5 mb-1" aria-hidden="true" />
      <span className="text-xs">Back</span>
    </button>
    <button
      className={`flex flex-col items-center ${active === 'home' ? 'text-primary-600 dark:text-primary-400' : 'text-text-secondary dark:text-text-dark-secondary hover:text-primary-600 dark:hover:text-primary-400'} focus:outline-none transition-colors`}
      aria-label="Go to home"
      aria-current={active === 'home' ? 'page' : undefined}
      onClick={onHome}
    >
      <Home className="w-5 h-5 mb-1" aria-hidden="true" />
      <span className="text-xs">Home</span>
    </button>
    <button
      className="flex flex-col items-center text-text-secondary dark:text-text-dark-secondary hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none transition-colors"
      aria-label="Open settings"
      onClick={onSettings}
    >
      <Settings className="w-5 h-5 mb-1" aria-hidden="true" />
      <span className="text-xs">Settings</span>
    </button>
  </nav>
);

const MobileSidebar = ({ open, onClose, canAccess, organizationsLoading, accessLoading, location, user, profileImageUrl, navigation, flags, isSystemAdmin }: { open: boolean; onClose: () => void; canAccess: (key: string) => boolean; organizationsLoading: boolean; accessLoading: boolean; location: ReturnType<typeof useLocation>; user?: { displayName?: string; username?: string; email?: string } | null; profileImageUrl?: string | null; navigation: typeof baseNavigation; flags: FlagMap; isSystemAdmin: () => boolean }) => {
  const navigate = useNavigate();
  
  return (
    <div
      className={`fixed inset-0 z-50 transition-transform duration-300 ${open ? '' : 'pointer-events-none'}`}
      role="dialog"
      aria-modal="true"
      aria-label="Navigation menu"
    >
      {/* Overlay */}
      <div
        className={`absolute inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`}
        aria-hidden="true"
        onClick={onClose}
      />
      {/* Drawer - Clean white/dark background like design */}
      <nav
        className={`mobile-sidebar absolute top-0 left-0 h-full w-80 shadow-xl transform transition-transform duration-300 overflow-y-auto ${open ? 'translate-x-0' : '-translate-x-full'}`}
        aria-label="Main navigation"
        style={{ backgroundColor: '#FFFFFF' }}
        data-testid="layout-mobile-sidebar"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary transition-colors"
          aria-label="Close navigation menu"
        >
          <X className="w-5 h-5" aria-hidden="true" />
        </button>

        {/* Profile Section */}
        <div className="pt-12 pb-6 px-6">
          <div className="flex items-center gap-4 mb-3">
            {/* Only show image if available, nothing otherwise */}
            {profileImageUrl && (
              <img 
                src={profileImageUrl} 
                alt="Profile" 
                className="w-14 h-14 rounded-full object-cover"
              />
            )}
            <div>
              <h2 className="text-lg font-bold text-text-primary dark:text-text-dark-primary">
                {user?.displayName || user?.username || 'User'}
              </h2>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                @{user?.username || 'user'}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Items */}
        <ul className="px-6 space-y-1">
          {navigation
            .filter(item => {
              if (item.key === 'budget' && !flags.budget_enabled && !isSystemAdmin()) {
                return false;
              }
              return (organizationsLoading || accessLoading) || 
                (canAccess(item.key) && !['non-profits', 'campaign-finance', 'sos-report', 'voter-registration'].includes(item.key));
            })
            .map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`flex items-center gap-4 px-2 py-3 text-base font-medium transition-colors ${
                      isActive 
                        ? 'text-primary-700 dark:text-primary-400' 
                        : 'text-text-primary dark:text-text-dark-primary hover:text-primary-600 dark:hover:text-primary-400'
                    }`}
                    onClick={onClose}
                  >
                    <Icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                </li>
              );
            })}
        </ul>

        {/* Divider */}
        <div className="my-6 mx-6 border-t border-border-light dark:border-border-dark" />

        {/* Settings at bottom */}
        <div className="px-6">
          <button
            onClick={() => { navigate('/profile'); onClose(); }}
            className="flex items-center gap-4 px-2 py-3 text-base font-medium text-text-primary dark:text-text-dark-primary hover:text-primary-600 dark:hover:text-primary-400 transition-colors w-full"
          >
            <Settings className="w-5 h-5" />
            Settings
          </button>
        </div>
      </nav>
    </div>
  );
};

// Impersonation state interface
interface ImpersonationState {
  isImpersonating: boolean;
  targetUserName: string;
  targetUserEmail: string;
  sessionTokenId: string;
}

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(null);
  const [profileImageError, setProfileImageError] = useState(false);
  const [desktopHeaderHeight, setDesktopHeaderHeight] = useState(64);
  const desktopHeaderRef = useRef<HTMLElement>(null);
  /* Measured rather than assumed: the banner wraps to two lines at narrow
     widths, so a hardcoded height would under-offset exactly where the overlap
     is worst. 0 when no simulation is active, so every offset below is a no-op
     for ordinary sessions. */
  const [simBannerHeight, setSimBannerHeight] = useState(0);
  const simBannerRef = useRef<HTMLDivElement>(null);
  const userMenuRef = React.useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check for popup mode (minimal layout without navigation)
  const isPopupMode = searchParams.get('popup') === 'true';
  const { theme, setTheme } = useTheme();
  const { resetUser, flags } = useFeatureFlags();
  const { pageMetadata } = usePageMetadata();

  // Back = the previous page whenever in-app history exists. When there is no
  // history (deep link, new tab), fall back to the page-declared backUrl,
  // then the dashboard — so Back never silently does nothing.
  const handleSmartBack = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else if (pageMetadata?.backUrl) {
      navigate(pageMetadata.backUrl);
    } else {
      navigate('/dashboard');
    }
  };

  // Fetch user organizations
  const { organizations, proOrganizations, loading: organizationsLoading, error: organizationsError, refetch: refetchOrganizations } = useOrganizations();

  // Fetch user access level (subscription-based)
  const { hasSubscriptionAccess, loading: accessLoading } = useUserAccess();

  // Fetch fresh profile data for profile image (presigned URLs expire)
  const { data: profileData, refetch: refetchProfile } = useQuery(
    ['user-profile-for-layout'],
    async () => {
      const response = await api.get('/users/profile');
      return response.data?.data;
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes - presigned URLs are valid for 1 hour
      refetchOnWindowFocus: true, // Refresh on tab focus to get fresh presigned URL
      retry: 1,
    }
  );
  const apiProfileImageUrl = profileData?.profileImageUrl || null;


  // Create role functions based on organizations and subscription access
  const { getUserRole, isSystemAdmin, canAccess } = createRoleFunctions(organizations, hasSubscriptionAccess);

  /* Offer the Citizen View only to accounts that actually have both views —
     read once per mount, since it changes only at login. Never offer it while
     simulating: the switch is a localStorage + hard-navigation, and it would
     carry the impersonation token into the citizen app. */
  const canSwitchToCitizen = React.useMemo(
    () => hasDualViewAccess() && !impersonation?.isImpersonating,
    [impersonation?.isImpersonating]
  );

  const navigation = baseNavigation;

  // Visibility for a desktop nav key: subscription/role gating + feature flags.
  const isNavKeyVisible = (key: string): boolean => {
    if (key === 'budget' && !flags.budget_enabled && !isSystemAdmin()) return false;
    return (organizationsLoading || accessLoading) || canAccess(key);
  };

  // Active match: exact path or a sub-route (so detail pages keep the parent highlighted/expanded).
  const isPathActive = (href: string): boolean =>
    location.pathname === href || location.pathname.startsWith(`${href}/`);

  // Which desktop group (if any) contains the active route.
  const activeGroupKey = desktopNavigation.find(
    (entry): entry is NavGroup => isNavGroup(entry) && entry.children.some((c) => isPathActive(c.href))
  )?.key ?? null;

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Auto-expand the group containing the active route (without collapsing user-opened groups).
  useEffect(() => {
    if (activeGroupKey) {
      setExpandedGroups((prev) => (prev[activeGroupKey] ? prev : { ...prev, [activeGroupKey]: true }));
    }
  }, [activeGroupKey]);

  const toggleGroup = (key: string) =>
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  // Renders a single desktop nav leaf (top-level item, group child, or collapsed icon).
  const renderNavLeaf = (leaf: NavLeaf, opts?: { indented?: boolean }) => {
    const isActive = isPathActive(leaf.href);
    return (
      <Link
        key={leaf.key}
        to={leaf.href}
        className={`w-full flex items-center ${
          sidebarCollapsed ? 'justify-center px-3' : `gap-3 ${opts?.indented ? 'pl-8 pr-4' : 'px-4'}`
        } py-3 rounded-xl text-left transition-all duration-200 font-medium relative hover:bg-white hover:bg-opacity-10 ${
          isActive ? 'nav-leaf-active text-white' : 'text-white/[.78]'
        }`}
        title={sidebarCollapsed ? leaf.name : undefined}
        aria-current={isActive ? 'page' : undefined}
      >
        {isActive && (
          <div
            className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-6 rounded-r bg-white"
            aria-hidden="true"
          ></div>
        )}
        <StatefulIcon icon={leaf.icon} active={isActive} size="md" className="flex-shrink-0" />
        {!sidebarCollapsed && <span>{leaf.name}</span>}
      </Link>
    );
  };

  // Compute current user info with fresh API profile image URL
  // Pro sidebar counts and lists the orgs the user WORKS in (#1516): the
  // dual-access citizen memberships (Capitol Commons Citizens, and the citizen
  // side of each pro org) are not working contexts.
  const currentUserInfo = getCurrentUser(proOrganizations.length, apiProfileImageUrl);

  useEffect(() => {
    const el = desktopHeaderRef.current;
    if (!el) return;
    // Border box, not contentRect: the header has a border-b, and a 1px
    // under-offset tucks the first row of content under it.
    const ro = new ResizeObserver(([entry]) => {
      setDesktopHeaderHeight(entry.target.getBoundingClientRect().height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Listen for profile image updates (matches organization pattern - refetch data)
  useEffect(() => {
    const handleProfileUpdate = () => {
      refetchProfile(); // Actually refetch profile data like organizations do
      setProfileImageError(false); // Reset error state when image is updated
    };

    window.addEventListener('profileImageUpdated', handleProfileUpdate);
    return () => window.removeEventListener('profileImageUpdated', handleProfileUpdate);
  }, [refetchProfile]);

  // Listen for organization logo updates
  useEffect(() => {
    const handleOrgLogoUpdate = () => {
      refetchOrganizations();
    };

    window.addEventListener('organizationLogoUpdated', handleOrgLogoUpdate);
    return () => window.removeEventListener('organizationLogoUpdated', handleOrgLogoUpdate);
  }, [refetchOrganizations]);

  // Check for impersonation state on mount
  useEffect(() => {
    const checkImpersonation = () => {
      const impersonationData = localStorage.getItem('impersonation');
      if (impersonationData) {
        try {
          const data = JSON.parse(impersonationData);
          setImpersonation({
            isImpersonating: true,
            targetUserName: data.targetUserName || 'User',
            targetUserEmail: data.targetUserEmail || '',
            sessionTokenId: data.sessionTokenId || ''
          });
        } catch (e) {
          console.error('Error parsing impersonation data:', e);
          localStorage.removeItem('impersonation');
        }
      }
    };

    checkImpersonation();

    // Listen for storage changes (in case impersonation starts in another tab)
    window.addEventListener('storage', checkImpersonation);
    return () => window.removeEventListener('storage', checkImpersonation);
  }, []);

  // Track the simulation banner's height so the chrome below can clear it.
  React.useEffect(() => {
    if (!impersonation?.isImpersonating) {
      setSimBannerHeight(0);
      return;
    }
    const el = simBannerRef.current;
    if (!el) return;

    const measure = () => setSimBannerHeight(el.getBoundingClientRect().height);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [impersonation?.isImpersonating]);

  // Handle exiting impersonation
  const handleExitImpersonation = useCallback(async () => {
    try {
      // Get the original admin token and impersonation data
      const originalToken = localStorage.getItem('originalAdminToken');
      const impersonationData = localStorage.getItem('impersonation');

      if (!originalToken) {
        // No original token found, just sign out
        localStorage.clear();
        navigate('/login');
        return;
      }

      // First, restore the original admin token (and account type) so API
      // calls and shell selection work as the admin again
      localStorage.setItem('token', originalToken);
      localStorage.removeItem('originalAdminToken');
      const originalAccountType = localStorage.getItem('originalAdminAccountType');
      if (originalAccountType) {
        localStorage.setItem('accountType', originalAccountType);
      }
      localStorage.removeItem('originalAdminAccountType');

      // Where to land after exiting — sessions store their origin; fall back
      // to the Accounts users tab (the legacy users page was removed).
      let returnTo = '/admin-next/accounts?tab=users';

      // Now log the end of impersonation session using the admin token
      if (impersonationData) {
        const data = JSON.parse(impersonationData);
        if (typeof data.returnTo === 'string' && data.returnTo.startsWith('/')) {
          returnTo = data.returnTo;
        }
        try {
          await api.post('/admin/impersonation/end', {
            sessionTokenId: data.sessionTokenId
          });
        } catch (e) {
          console.error('Error logging impersonation end:', e);
          // Continue even if logging fails
        }
      }

      // Clear impersonation data
      localStorage.removeItem('impersonation');

      // Clear ALL session-derived state — user, org/membership context,
      // available views, and the persisted query cache — so nothing from the
      // simulated session leaks into the restored admin session (stale role
      // chips, denied admin access, cached workspace data).
      localStorage.removeItem('user');
      localStorage.removeItem('organization');
      localStorage.removeItem('membership');
      localStorage.removeItem('availableViews');
      window.dispatchEvent(new Event('auth:cleared'));

      /* Put the admin's own identity back. These four keys are written only by
         setAuthSession() from a login response, so clearing them left a valid
         admin token with no identity behind it — the header fell back to a "U"
         avatar and getUserRole() returned its 'user' default, taking Admin Tools
         with it. startUserSimulation() snapshots them; restore after the clear
         above so the simulated session's values cannot survive. */
      const adminSnapshot = localStorage.getItem(ADMIN_SESSION_SNAPSHOT_KEY);
      if (adminSnapshot) {
        try {
          const restored = JSON.parse(adminSnapshot) as Record<string, string>;
          for (const [key, value] of Object.entries(restored)) {
            if (typeof value === 'string') localStorage.setItem(key, value);
          }
        } catch (e) {
          // A corrupt snapshot is not worth blocking the exit: the token is
          // already restored, so a re-login rebuilds the rest.
          console.error('Error restoring admin session snapshot:', e);
        }
      }
      localStorage.removeItem(ADMIN_SESSION_SNAPSHOT_KEY);
      try {
        // Await the IndexedDB wipe so the reload below can't rehydrate stale data
        await indexedDbPersister.removeClient();
      } catch (persistErr) {
        console.error('Error clearing persisted cache on exit simulation:', persistErr);
      }

      // Reset state
      setImpersonation(null);

      // Redirect back to where the simulation was started, with full page reload
      window.location.href = returnTo;
    } catch (error) {
      console.error('Error exiting impersonation:', error);
      // Force a clean state
      localStorage.clear();
      navigate('/login');
    }
  }, [navigate]);

  // Accessibility: close sidebar on Escape
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    if (sidebarOpen || userMenuOpen) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen, userMenuOpen]);

  // Close user menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Close user menu when navigating to a different page
  React.useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Sign out handler
  const handleSignOut = () => {
    // Reset LaunchDarkly to anonymous context before clearing storage
    resetUser().catch(() => {});
    // Clear localStorage/sessionStorage/cookies as needed
    localStorage.clear();
    sessionStorage.clear();
    navigate('/');
    window.location.reload(); // Ensure state is reset
  };

  // Calculate top offset when impersonation banner is visible
  const impersonationOffset = impersonation?.isImpersonating ? 'pt-12' : '';

  // A failed organizations fetch silently strips role-gated menu entries (the
  // app looks logged-in-but-demoted). Never let that state pass without a
  // visible explanation and a retry.
  const orgLoadErrorBanner = organizationsError && !organizationsLoading ? (
    <div
      role="alert"
      className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border-l-4 border-warning-500 bg-warning-50 p-3 text-sm dark:bg-warning-900 dark:bg-opacity-20"
    >
      <AlertTriangle className="h-5 w-5 shrink-0 text-warning-600 dark:text-warning-400" />
      <span className="flex-1 text-text-secondary dark:text-text-dark-secondary">
        Couldn't load your organizations — navigation and permissions may be incomplete.
      </span>
      <button
        onClick={() => refetchOrganizations()}
        className="font-medium text-primary-700 hover:underline dark:text-primary-400"
      >
        Retry
      </button>
    </div>
  ) : null;

  // Popup mode: Minimal layout without navigation sidebar
  if (isPopupMode) {
    return (
      <div className="min-h-screen bg-background dark:bg-background-dark-primary">
        <main id="main-content" className="p-4 md:p-6">
          {children}
        </main>
      </div>
    );
  }

  return (
    <>
      {/* Simulation banner — pinned here, not by the banner itself, so its height
          can be measured and the chrome below shifted down by exactly that much. */}
      {impersonation?.isImpersonating && (
        <div ref={simBannerRef} className="fixed top-0 left-0 right-0 z-[9999]">
          <ImpersonationBanner
            userName={impersonation.targetUserName}
            userEmail={impersonation.targetUserEmail}
            onExitImpersonation={handleExitImpersonation}
          />
        </div>
      )}

      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-700 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Mobile Layout */}
      <div className="lg:hidden">
        {/* Header - Clean modern style with page titles */}
        <header
          className="mobile-header fixed top-0 left-0 w-full h-16 md:h-20 z-40 bg-white shadow-nav border-b border-border-light dark:border-transparent flex items-center px-4 pt-2"
          style={{ top: simBannerHeight }}
          data-testid="layout-mobile-header"
        >
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="text-text-secondary dark:text-text-dark-secondary hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none transition-colors mr-3 flex-shrink-0"
            aria-label="Open navigation menu"
          >
            <Menu className="w-6 h-6" aria-hidden="true" />
          </button>

          {/* Page logo and title or app name */}
          <div className="flex-1 flex items-center gap-2 min-w-0">
            {/* Page Logo (e.g., workspace logo) */}
            {pageMetadata?.logoUrl && (
              <img
                src={pageMetadata.logoUrl}
                alt=""
                className="w-8 h-8 rounded-lg object-contain bg-white dark:bg-background-dark-secondary border border-border-light dark:border-border-dark flex-shrink-0"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            {pageMetadata ? (
              <div className="flex flex-col justify-center min-w-0">
                <h1 className="text-lg font-bold text-primary-700 dark:text-primary-400 truncate">
                  {pageMetadata.title}
                </h1>
                {pageMetadata.description && (
                  <p className="hidden md:block text-xs text-text-secondary dark:text-text-dark-secondary truncate">
                    {pageMetadata.description}
                  </p>
                )}
              </div>
            ) : (
              <span className="font-bold text-xl text-primary-700 dark:text-primary-400 font-poppins">Capitol Commons</span>
            )}
          </div>

          {/* Capitol Commons Logomark */}
          <div className="ml-3 flex-shrink-0">
            <img
              src={logomark}
              alt="Capitol Commons"
              className="h-7 w-auto"
              aria-hidden="true"
            />
          </div>
        </header>
        {/* Sidebar Drawer */}
        <MobileSidebar 
          open={mobileSidebarOpen} 
          onClose={() => setMobileSidebarOpen(false)} 
          canAccess={canAccess} 
          organizationsLoading={organizationsLoading} 
          accessLoading={accessLoading} 
          location={location}
          user={{ displayName: currentUserInfo.name, username: currentUserInfo.name }}
          profileImageUrl={currentUserInfo.profileImageUrl}
          navigation={navigation}
          flags={flags}
          isSystemAdmin={isSystemAdmin}
        />
        {/* Footer Nav */}
        <MobileFooterNav
          active={location.pathname === '/dashboard' ? 'home' : 'back'}
          onBack={handleSmartBack}
          onHome={() => navigate('/dashboard')}
          onSettings={() => navigate('/profile')}
        />
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:block">
        {/* Sidebar - Clean without borders */}
        <nav
          id="sidebar"
          className={`sidebar-shell flex flex-col fixed top-0 left-0 h-screen z-50 transition-all duration-200 ${sidebarCollapsed ? 'w-16' : 'w-64'
            }`}
          style={{ top: simBannerHeight, height: `calc(100vh - ${simBannerHeight}px)` }}
          aria-label="Main navigation"
          data-testid="layout-desktop-sidebar"
        >
          {/* App Title Section — the wordmark links out to the marketing
              site (new tab, so nobody loses their working session). Collapsed
              rail gets the square logomark instead; the wordmark can't read
              at 64px. */}
          <div
            className="flex items-center justify-center h-20 px-4 pt-6 pb-3"
          >
            <a
              href="https://capitolcommons.ai"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Capitol Commons — capitolcommons.ai (opens in a new tab)"
              className="flex items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
            >
              {sidebarCollapsed ? (
                <img src={logomark} alt="Capitol Commons" className="h-8 w-8" />
              ) : (
                <img src={ccLogo} alt="Capitol Commons" className="h-11 w-auto" />
              )}
            </a>
          </div>

          {/* Collapse/Expand button */}
          <button
            className="flex items-center justify-end h-12 w-full pr-4 transition-all duration-200 text-white hover:bg-white hover:bg-opacity-10"
            onClick={() => setSidebarCollapsed((c) => !c)}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronsRight className="w-5 h-5" /> : <ChevronsLeft className="w-5 h-5" />}
          </button>

          <div className="flex flex-col h-full pt-2 overflow-y-auto">
            {/* Navigation */}
            <div className={`flex-1 space-y-1 ${sidebarCollapsed ? 'px-2 mt-4' : 'px-4 mt-6'}`}>
              {sidebarCollapsed
                ? desktopNavigation
                    .flatMap((entry) => (isNavGroup(entry) ? entry.children : [entry as NavLeaf]))
                    .filter((leaf) => isNavKeyVisible(leaf.key))
                    .map((leaf) => renderNavLeaf(leaf))
                : (() => {
                    // Hairlines split the nav into three bands (before the
                    // Legislative Cycle group and before Reports). A divider
                    // renders only if something rendered since the last one,
                    // so filtered-out bands can't produce leading or doubled
                    // rules. The collapsed icon rail (branch above) skips
                    // dividers entirely.
                    let sinceDivider = 0;
                    const out: React.ReactNode[] = [];
                    desktopNavigation.forEach((entry) => {
                      const visible = isNavGroup(entry)
                        ? entry.children.some((child) => isNavKeyVisible(child.key))
                        : isNavKeyVisible(entry.key);
                      if (!visible) return;
                      if ((entry.key === 'legislative-cycle' || entry.key === 'sos-report') && sinceDivider > 0) {
                        out.push(<div key={`divider-${entry.key}`} className="nav-divider" aria-hidden="true" />);
                        sinceDivider = 0;
                      }
                      sinceDivider++;
                      if (!isNavGroup(entry)) {
                        out.push(renderNavLeaf(entry as NavLeaf));
                        return;
                      }
                      const visibleChildren = entry.children.filter((child) => isNavKeyVisible(child.key));
                      const isExpanded = !!expandedGroups[entry.key];
                      const isGroupActive = activeGroupKey === entry.key;
                      out.push(
                        <div key={entry.key}>
                        <button
                          type="button"
                          onClick={() => toggleGroup(entry.key)}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 font-medium hover:bg-white hover:bg-opacity-10 ${
                            isGroupActive ? 'text-white' : 'text-white/[.78]'
                          } ${isGroupActive && !isExpanded ? 'bg-white bg-opacity-10' : ''}`}
                          aria-expanded={isExpanded}
                        >
                          <StatefulIcon icon={entry.icon} active={isGroupActive} size="md" className="flex-shrink-0" />
                          <span className="flex-1">{entry.name}</span>
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                          ) : (
                            <ChevronRight className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                          )}
                        </button>
                        {isExpanded && (
                          <div className="mt-1 space-y-1">
                            {visibleChildren.map((child) => renderNavLeaf(child, { indented: true }))}
                          </div>
                        )}
                      </div>
                      );
                    });
                    return out;
                  })()}
            </div>

            {/* Organizations section - only show when expanded */}
            {!sidebarCollapsed && (
              <div
                className="mt-8 px-4 pt-6 border-t border-white border-opacity-20"
              >
                <div
                  className="text-xs font-semibold mb-3 tracking-wider uppercase text-white text-opacity-80"
                >
                  Organizations
                </div>
                {organizationsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="animate-pulse">
                        <div
                          className="h-4 rounded w-24 mb-2 bg-white bg-opacity-10"
                        ></div>
                        <div
                          className="h-3 rounded w-16 bg-white bg-opacity-10"
                        ></div>
                      </div>
                    ))}
                  </div>
                ) : proOrganizations.length > 0 ? (
                  <ul className="space-y-3">
                    {proOrganizations.map((org) => {
                      // Orgs the user administers open that org's settings —
                      // the only path to any admin org after the first.
                      const settingsUrl = org.role?.toLowerCase() === 'org_admin' && hasSubscriptionAccess
                        ? `/organization-settings?org=${encodeURIComponent(org.id)}`
                        : null;
                      const card = (
                        <div className="flex items-center gap-3">
                          {/* Organization Logo */}
                          {org.logo_url ? (
                            <img
                              src={org.logo_url}
                              alt={`${org.name} logo`}
                              className="w-10 h-10 rounded object-cover flex-shrink-0 bg-white bg-opacity-20"
                              onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                img.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                              <Briefcase className="w-5 h-5 text-white text-opacity-70" />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <div
                              className="font-medium text-sm truncate text-white"
                            >
                              {org.name}
                            </div>
                            <div
                              className="text-xs font-medium uppercase tracking-wide text-white text-opacity-70"
                            >
                              {org.role}
                            </div>
                          </div>
                        </div>
                      );
                      return (
                        <li key={org.id}>
                          {settingsUrl ? (
                            <Link
                              to={settingsUrl}
                              title={`${org.name} settings`}
                              className="block rounded-lg p-3 bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors"
                            >
                              {card}
                            </Link>
                          ) : (
                            <div className="rounded-lg p-3 bg-white bg-opacity-10">
                              {card}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div
                    className="text-xs italic rounded-lg p-3 text-center bg-white bg-opacity-10 text-white text-opacity-70"
                  >
                    No organizations
                  </div>
                )}
              </div>
            )}

            {/* Copyright - only show when expanded */}
            {!sidebarCollapsed && (
              <div
                className="mt-auto p-4 text-xs text-white text-opacity-60"
              >
                &copy; {new Date().getFullYear()} Capitol Commons
              </div>
            )}
          </div>
        </nav>

        {/* Fixed Top Header (desktop only) - starts after sidebar */}
        <header
          ref={desktopHeaderRef}
          className={`hidden lg:flex fixed top-0 right-0 z-40 bg-background dark:bg-background-dark-primary flex-col transition-all duration-200 border-b border-border-light dark:border-border-dark shadow-sm`}
          style={{
            left: sidebarCollapsed ? '4rem' : '16rem',
            top: simBannerHeight
          }}
          data-testid="layout-desktop-header"
        >
          {/* Top row: title + user actions */}
          <div className="flex items-center px-4 pt-3 pb-1">
            {/* Left section: Logo, Page title and description */}
            <div className="flex-1 flex items-center gap-3 min-w-0">
              {/* Page Logo (e.g., workspace logo) */}
              {pageMetadata?.logoUrl && (
                <img
                  src={pageMetadata.logoUrl}
                  alt=""
                  className="w-10 h-10 rounded-lg object-contain bg-white dark:bg-background-dark-secondary border border-border-light dark:border-border-dark flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              {pageMetadata && (
                <div className="flex flex-col justify-center min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <h1 className="text-3xl font-bold text-text-primary dark:text-text-dark-primary truncate">
                      {pageMetadata.title}
                    </h1>
                    {pageMetadata.helpContentKey && (
                      <HelpTrigger contentKey={pageMetadata.helpContentKey} size="md" />
                    )}
                  </div>
                  {pageMetadata.description && (
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary truncate">
                      {pageMetadata.description}
                    </p>
                  )}
                </div>
              )}
            </div>
          {/* Right section: notification, user initials, user info */}
          <div className="flex items-center space-x-6">
            <NotificationsBell />
            <div ref={userMenuRef} className="relative flex items-center space-x-3">
              <button
                className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center text-lg font-bold text-primary-700 dark:text-primary-300 border-2 border-primary-200 dark:border-primary-800 focus:outline-none hover:border-primary-300 dark:hover:border-primary-700 transition-colors overflow-hidden"
                onClick={() => setUserMenuOpen((open) => !open)}
                aria-haspopup="true"
                aria-expanded={userMenuOpen}
                aria-label={`User menu for ${currentUserInfo.name}`}
                tabIndex={0}
              >
                {currentUserInfo.profileImageUrl && !profileImageError ? (
                  <img
                    src={currentUserInfo.profileImageUrl}
                    alt={`Profile photo of ${currentUserInfo.name}`}
                    className="w-full h-full object-cover"
                    onError={() => setProfileImageError(true)}
                  />
                ) : (
                  <span aria-label={`${currentUserInfo.name} initials`}>{currentUserInfo.initials}</span>
                )}
              </button>
              <div className="hidden sm:flex flex-col items-start text-left">
                <span className="text-text-primary dark:text-text-dark-primary font-semibold leading-tight">{currentUserInfo.name}</span>
                <span className="text-text-tertiary dark:text-text-dark-tertiary text-xs leading-tight">{currentUserInfo.orgCount} Organizations</span>
              </div>
              {/* User dropdown menu (role-based actions) */}
              {userMenuOpen && (
                <div className="absolute right-0 top-12 mt-2 w-56 card p-0 z-50 overflow-hidden">
                  <Link
                    to="/profile"
                    className="block w-full text-left px-4 py-3 text-sm text-text-primary dark:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Profile Settings
                  </Link>

                  {/* Organization Settings — org admins with subscription access.
                      System admins are included because getUserRole() reports
                      the highest-priority role: a strict === 'org_admin' hid the
                      link from anyone who also held a system_admin membership,
                      leaving the URL as their only way in. The route enforces
                      the same pair via requireOrgAdmin. */}
                  {['org_admin', 'system_admin'].includes(getUserRole()) && hasSubscriptionAccess && (
                    <Link
                      to="/organization-settings"
                      className="block w-full text-left px-4 py-3 text-sm text-text-primary dark:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      Organization Settings
                    </Link>
                  )}

                  {/* Citizen View — only for accounts that actually have both views.
                      A hard navigation, not a <Link>: the citizen app has its own
                      layout, theme and providers, and accountType is read during
                      bootstrap, so a client-side route change would leave this shell
                      mounted around it. Shares switchToCitizenView with the
                      post-login chooser so both agree on what switching means. */}
                  {canSwitchToCitizen && (
                    <button
                      className="flex items-center w-full text-left px-4 py-3 text-sm text-text-primary dark:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                      onClick={() => {
                        setUserMenuOpen(false);
                        switchToCitizenView();
                      }}
                    >
                      <User className="w-4 h-4 mr-3 flex-shrink-0" aria-hidden="true" />
                      Citizen View
                    </button>
                  )}

                  {/* Theme Selector */}
                  <div className="border-t border-border-light dark:border-border-dark">
                    <button
                      className="flex items-center w-full text-left px-4 py-3 text-sm text-text-primary dark:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                      onClick={() => {
                        setTheme('light');
                        setUserMenuOpen(false);
                      }}
                      aria-label="Switch to light mode"
                      aria-current={theme === 'light' ? 'true' : undefined}
                    >
                      <StatefulIcon icon={Sun} active={theme === 'light'} size="sm" className="mr-3" />
                      Light Mode
                      {theme === 'light' && <span className="ml-auto text-primary-600 font-medium" aria-label="Currently selected">✓</span>}
                    </button>
                    <button
                      className="flex items-center w-full text-left px-4 py-3 text-sm text-text-primary dark:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                      onClick={() => {
                        setTheme('dark');
                        setUserMenuOpen(false);
                      }}
                      aria-label="Switch to dark mode"
                      aria-current={theme === 'dark' ? 'true' : undefined}
                    >
                      <StatefulIcon icon={Moon} active={theme === 'dark'} size="sm" className="mr-3" />
                      Dark Mode
                      {theme === 'dark' && <span className="ml-auto text-primary-600 font-medium" aria-label="Currently selected">✓</span>}
                    </button>
                  </div>

                  {/* Admin Tools - Only show for SYSTEM_ADMIN */}
                  {isSystemAdmin() && (
                    <div className="border-t border-border-light dark:border-border-dark">
                      <Link
                        to="/admin"
                        className="block w-full text-left px-4 py-3 text-sm text-text-primary dark:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Admin Tools
                      </Link>
                    </div>
                  )}

                  <div className="border-t border-border-light dark:border-border-dark">
                    <button
                      className="w-full text-left px-4 py-3 text-sm text-text-primary dark:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                      onClick={handleSignOut}
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
          {/* Back button row — inside fixed header, below title row */}
          {location.pathname !== '/dashboard' && location.pathname !== '/home' && (
            <div className="px-4 pb-1">
              <button
                onClick={handleSmartBack}
                className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                aria-label="Go back to previous page"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </button>
            </div>
          )}
        </header>

      </div>

      {/*
        THE page mount — exactly one. The mobile and desktop chromes above are
        position:fixed overlays, so this main only needs the offsets each chrome
        used to give its own copy: pt-16/pb-20 clearing the mobile header and tab
        bar, and at lg+ the sidebar margin plus the measured desktop header
        height (a CSS var, because a style prop cannot be breakpoint-scoped).

        Layout previously rendered {children} inside BOTH breakpoint branches,
        CSS-hiding one — so every page mounted twice, with independent state and
        duplicated fetches, and the skip-link's #main-content target resolved to
        the hidden copy. Do not move children back inside a branch.
      */}
      <div className={impersonationOffset}>
        <main
          id="main-content"
          role="main"
          className={`h-screen overflow-y-auto bg-white dark:bg-background-dark-primary pt-16 md:pt-20 pb-20 lg:bg-background lg:pb-0 lg:pt-[var(--desktop-header-height)] lg:transition-all lg:duration-200 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}
          style={{
            '--desktop-header-height': `${desktopHeaderHeight}px`,
            // Start below the banner and give back the height it took, so the
            // scroll container is still exactly the visible area.
            marginTop: simBannerHeight,
            height: `calc(100vh - ${simBannerHeight}px)`,
          } as React.CSSProperties}
        >
          <div className="lg:pt-2 lg:pb-4 lg:px-4">
            {orgLoadErrorBanner && (
              <div className="px-4 pt-2 lg:px-0 lg:pt-0">{orgLoadErrorBanner}</div>
            )}
            {children}
          </div>
        </main>
      </div>
    </>
  );
};

export default Layout;
